import { useState, useMemo } from 'react';
import { Pie, PieChart, Sector, PieSectorDataItem, Tooltip, TooltipIndex, Cell } from 'recharts';
import { RechartsDevtools } from '@recharts/devtools';
import ChartCard from './ChartCard';
import { theme } from '../../theme.js';

// #region Sample data
const defaultData = [];

// #endregion
const renderActiveShape = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
  value,}: PieSectorDataItem) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * (midAngle ?? 1));
  const cos = Math.cos(-RADIAN * (midAngle ?? 1));
  const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
  const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
  const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
  const my = (cy ?? 0) + ((outerRadius ?? 0) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 6}
        outerRadius={(outerRadius ?? 0) + 10}
        fill={fill}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333" fontSize="12" fontWeight="bold">{`${payload.name}: ${value}`}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999" fontSize="12">
        {`(${((percent ?? 1) * 100).toFixed(1)}%)`}
      </text>
    </>
  );
};

const CenterDisplay = ({ cx, cy, activeIndex, data, total }) => {
  return (
    <>
      <text 
        x={cx} 
        y={cy} 
        textAnchor="middle" 
        fontSize="28" 
        fontWeight="bold" 
        fill={theme.colors.textInverted}
      >
        ${total.toFixed(2)}
      </text>
      <text 
        x={cx} 
        y={cy + 25} 
        textAnchor="middle" 
        fontSize="12" 
        fill={theme.colors.textMuted}
      >
        Total
      </text>
    </>
  );
};

export default function ChartPie({
  data = [],
  isAnimationActive = true,
  defaultIndex = 0,
}) {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  
  // Process data for pie chart
  const chartData = useMemo(() => {
    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : [];
    
    if (!dataArray || dataArray.length === 0) {
      return defaultData;
    }
    
    // Transform revenue data for pie chart
    return dataArray.map(item => ({
      name: item.item_type === 'option' && item.option_name ? item.option_name : (item.item_type === 'bib' ? 'Bibs' : item.item_type),
      value: item.total_revenue || 0,
      ...item
    }));
  }, [data]);
  
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [chartData]);

  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
    '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c', '#d084d0'
  ];

  return (
    <ChartCard>
      <div style={styles.container}>
        <PieChart
          style={{ width: '500px', height: '500px' }}
          margin={{
            top: 50,
            right: 120,
            bottom: 50,
            left: 120,
          }}
        >
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="3" floodOpacity="0.3" />
            </filter>
          </defs>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="80%"
            dataKey="value"
            isAnimationActive={isAnimationActive}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(defaultIndex)}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={() => null} />
          <g>
            <CenterDisplay cx={250} cy={250} activeIndex={activeIndex} data={chartData} total={total} />
          </g>
          <RechartsDevtools />
        </PieChart>

        {/* Fixed Legend */}
        <div style={styles.legend}>
          <h3 style={styles.legendTitle}>Légende</h3>
          {chartData.map((item, index) => (
            <div 
              key={item.name}
              style={{
                ...styles.legendItem,
                opacity: activeIndex === index ? 1 : 0.7,
                backgroundColor: activeIndex === index ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
              }}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(defaultIndex)}
            >
              <div
                style={{
                  ...styles.legendColor,
                  backgroundColor: COLORS[index % COLORS.length],
                }}
              />
              <div style={styles.legendContent}>
                <div style={styles.legendName}>{item.name}</div>
                <div style={styles.legendValue}>
                  ${item.value.toFixed(2)} ({((item.value / total) * 100).toFixed(1)}%)
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

const styles = {
  container: {
    display: 'flex',
    gap: '2rem',
    alignItems: 'flex-start',
  },
  legend: {
    flex: 1,
    minWidth: '250px',
    maxWidth: '350px',
    padding: '1.5rem',
    backgroundColor: '#FFFFFF',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  legendTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: '1rem',
    marginTop: 0,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  legendColor: {
    width: '16px',
    height: '16px',
    borderRadius: '0.25rem',
    flexShrink: 0,
  },
  legendContent: {
    flex: 1,
  },
  legendName: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: '0.25rem',
  },
  legendValue: {
    fontSize: '0.75rem',
    color: '#6B7280',
  },
};